import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/heroSlides";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);

  const go = (next: number) => setIndex((next + heroSlides.length) % heroSlides.length);

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % heroSlides.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [index]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(index + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden md:h-[600px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.videoId}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img src={slide.image} alt={slide.headline} title={`${slide.headline} 主播视频`} className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/70 to-background-950/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full items-center px-4 md:px-6">
        <div className="max-w-2xl">
          <div key={index} className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-primary-500/10 px-3 py-1 text-xs font-semibold text-primary-300">
              <span className="h-1.5 w-1.5 animate-pulse-live rounded-full bg-primary-500" />
              {heroSlides[index].badge} · {heroSlides[index].kicker}
            </span>
            <h2 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-foreground-50 md:text-5xl">
              {heroSlides[index].headline}
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-300 md:text-base">
              {heroSlides[index].subline}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/video/${heroSlides[index].videoId}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-bold text-background-950 transition-colors hover:bg-primary-400"
              >
                <i className="ri-play-fill text-lg" />
                立即观看
              </Link>
              <Link
                to="/search"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 bg-background-900/70 px-5 py-3 font-label text-sm font-semibold text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-300"
              >
                <i className="ri-compass-3-line text-lg" />
                发现更多视频
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 md:left-auto md:right-6 md:translate-x-0">
        <button
          type="button"
          aria-label="上一个"
          onClick={() => go(index - 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-600 bg-background-900/70 text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-300"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        {heroSlides.map((s, i) => (
          <button
            key={s.videoId}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => go(i)}
            className={`h-2 rounded-full transition-all ${
              i === index ? "w-6 bg-primary-500" : "w-2 bg-background-500 hover:bg-foreground-500"
            }`}
          />
        ))}
        <button
          type="button"
          aria-label="下一个"
          onClick={() => go(index + 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-600 bg-background-900/70 text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-300"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>
    </section>
  );
}